import {
  selectionEconomics,
  type PlacementShortage,
  type SimulationLogEntry,
  type YearPlanSimulationInput
} from './yearPlanSimulationService';

// Why some of a template's courses found no room, in the planner's terms
// rather than the algorithm's: which resource ran out and over which weeks,
// not by how many hours.
export type SimulationShortfallReason =
  | { kind: 'beds'; fromWeekNumber: number; toWeekNumber: number }
  | { kind: 'staff'; professionId: string; fromWeekNumber: number; toWeekNumber: number }
  | { kind: 'too-long' }
  | { kind: 'without-weeks' }
  | { kind: 'no-room' };

export interface SimulationProgressRow {
  templateId: string;
  templateName: string;
  requestedCourseCount: number;
  placedCourseCount: number;
  unplacedCourseCount: number;
  placedPatientCount: number;
  placedRevenue: number;
  reasons: SimulationShortfallReason[];
}

// The run so far, a row per selected template plus the totals across them.
export interface SimulationProgress {
  rows: SimulationProgressRow[];
  requestedCourseCount: number;
  processedCourseCount: number;
  placedCourseCount: number;
  placedPatientCount: number;
  placedRevenue: number;
}

interface RowTally {
  templateId: string;
  templateName: string;
  requestedCourseCount: number;
  capacity: number;
  placedCourseCount: number;
  unplacedCourseCount: number;
  shortages: (PlacementShortage | null)[];
  skipReason: SimulationShortfallReason | null;
}

interface WeekRange {
  fromWeekNumber: number;
  toWeekNumber: number;
}

const widen = (range: WeekRange | undefined, weekNumber: number): WeekRange => ({
  fromWeekNumber: Math.min(range?.fromWeekNumber ?? weekNumber, weekNumber),
  toWeekNumber: Math.max(range?.toWeekNumber ?? weekNumber, weekNumber)
});

// One reason per resource: beds first, then each profession in the order it
// first ran short, each spanning the weeks its shortages fell in.
function collectReasons(tally: RowTally): SimulationShortfallReason[] {
  if (tally.skipReason) return [tally.skipReason];
  let beds: WeekRange | undefined;
  const staff = new Map<string, WeekRange>();
  let hasUnexplained = false;
  for (const shortage of tally.shortages) {
    if (!shortage) hasUnexplained = true;
    else if (shortage.kind === 'beds') beds = widen(beds, shortage.weekNumber);
    else staff.set(shortage.professionId, widen(staff.get(shortage.professionId), shortage.weekNumber));
  }
  const reasons: SimulationShortfallReason[] = [];
  if (beds) reasons.push({ kind: 'beds', ...beds });
  for (const [professionId, range] of staff) reasons.push({ kind: 'staff', professionId, ...range });
  if (hasUnexplained) reasons.push({ kind: 'no-room' });
  return reasons;
}

function applyEntry(tallies: ReadonlyMap<string, RowTally>, entry: SimulationLogEntry): void {
  if (!('templateId' in entry)) return;
  const tally = tallies.get(entry.templateId);
  if (!tally) return;
  switch (entry.kind) {
    case 'placed':
      tally.placedCourseCount += 1;
      break;
    case 'unplaced':
      tally.unplacedCourseCount += 1;
      tally.shortages.push(entry.shortage);
      break;
    // A template the period cannot hold is skipped whole: none of its courses
    // is tried, so all of them count as without room at once.
    case 'template-too-long':
    case 'template-without-weeks':
      tally.unplacedCourseCount = tally.requestedCourseCount;
      tally.skipReason = { kind: entry.kind === 'template-too-long' ? 'too-long' : 'without-weeks' };
      break;
  }
}

// Tallies the steps logged so far into a row per selected template, in the
// order they were selected. Rows exist from the start, so a template the run
// has not reached yet shows at nought rather than appearing later.
export function summarizeSimulationProgress(
  input: YearPlanSimulationInput,
  log: readonly SimulationLogEntry[]
): SimulationProgress {
  const tallies = new Map<string, RowTally>();
  for (const selection of input.selections) {
    const template = input.templates.find((t) => t.id === selection.templateId);
    if (!template) continue;
    tallies.set(template.id, {
      templateId: template.id,
      templateName: template.name,
      requestedCourseCount: selection.courseCount,
      capacity: selection.capacity,
      placedCourseCount: 0,
      unplacedCourseCount: 0,
      shortages: [],
      skipReason: null
    });
  }
  for (const entry of log) applyEntry(tallies, entry);

  const rows = [...tallies.values()].map((tally): SimulationProgressRow => {
    const template = input.templates.find((t) => t.id === tally.templateId);
    const placed = template
      ? selectionEconomics(template, tally.placedCourseCount, tally.capacity)
      : { patientCount: 0, revenue: 0 };
    return {
      templateId: tally.templateId,
      templateName: tally.templateName,
      requestedCourseCount: tally.requestedCourseCount,
      placedCourseCount: tally.placedCourseCount,
      unplacedCourseCount: tally.unplacedCourseCount,
      placedPatientCount: placed.patientCount,
      placedRevenue: placed.revenue,
      reasons: collectReasons(tally)
    };
  });

  return {
    rows,
    requestedCourseCount: rows.reduce((sum, r) => sum + r.requestedCourseCount, 0),
    processedCourseCount: rows.reduce(
      (sum, r) => sum + r.placedCourseCount + r.unplacedCourseCount,
      0
    ),
    placedCourseCount: rows.reduce((sum, r) => sum + r.placedCourseCount, 0),
    placedPatientCount: rows.reduce((sum, r) => sum + r.placedPatientCount, 0),
    placedRevenue: rows.reduce((sum, r) => sum + r.placedRevenue, 0)
  };
}
